import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { api, formatApiErrorDetail } from "@/lib/api";
import PhoneInput from "@/components/PhoneInput";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, ChevronDown, Loader2, ShieldCheck, Sparkles } from "lucide-react";

/**
 * Book-a-demo signup page. Split-screen layout matching the /login aesthetic.
 * IP-based country detection pre-fills the phone prefix. reCAPTCHA v2 protects
 * the submission. On success, a success card replaces the form.
 */
const JOB_ROLES = [
    { v: "owner", l: "Owner" },
    { v: "general_manager", l: "General Manager" },
    { v: "operations_manager", l: "Operations Manager" },
    { v: "front_desk_manager", l: "Front Desk Manager" },
    { v: "it", l: "IT" },
    { v: "consultant", l: "Consultant" },
    { v: "other", l: "Other" },
];

const PROPERTY_TYPES = [
    { v: "hotel", l: "Hotel" },
    { v: "boutique_hotel", l: "Boutique Hotel" },
    { v: "hostel", l: "Hostel" },
    { v: "apartments", l: "Apartments" },
    { v: "villa", l: "Villa" },
    { v: "bed_and_breakfast", l: "Bed & Breakfast" },
    { v: "resort", l: "Resort" },
    { v: "guest_house", l: "Guest House" },
    { v: "other", l: "Other" },
];

const COUNTRIES = [
    "Albania", "Kosovo", "North Macedonia", "Montenegro", "Greece", "Italy",
    "Germany", "Austria", "Switzerland", "United Kingdom", "France", "Spain",
    "Portugal", "Netherlands", "Belgium", "Ireland", "Sweden", "Denmark",
    "Norway", "Finland", "Poland", "Czech Republic", "Slovakia", "Hungary",
    "Romania", "Bulgaria", "Croatia", "Slovenia", "Serbia", "Bosnia and Herzegovina",
    "Turkey", "United States", "Canada", "United Arab Emirates", "Saudi Arabia",
    "Australia", "Other",
];

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

export default function Signup() {
    // Wrap the form in the reCAPTCHA v3 provider so useGoogleReCaptcha works.
    if (!RECAPTCHA_SITE_KEY) return <SignupForm />;
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={RECAPTCHA_SITE_KEY}
            scriptProps={{ async: true, defer: true, appendTo: "head" }}
        >
            <SignupForm />
        </GoogleReCaptchaProvider>
    );
}

function SignupForm() {
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [detectedCountry, setDetectedCountry] = useState("AL");
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        phone_country: "AL",
        job_role: "",
        property_name: "",
        property_type: "",
        country: "",
        heard_about: "",
        consent: true,
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    // IP-based geo — pre-select phone prefix + country dropdown
    useEffect(() => {
        api.get("/public/geo").then(({ data }) => {
            if (data?.country_code) {
                setDetectedCountry(data.country_code);
                setForm((f) => ({
                    ...f,
                    phone_country: data.country_code,
                    // If the visitor's country name matches one in the list, preselect it
                    country: COUNTRIES.find((c) => c.toLowerCase() === (data.country_name || "").toLowerCase()) || f.country,
                }));
            }
        }).catch(() => { /* ignore — default stays AL */ });
    }, []);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const validate = () => {
        const e = {};
        if (!form.first_name.trim()) e.first_name = "Required";
        if (!form.last_name.trim()) e.last_name = "Required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email";
        if (!form.phone || form.phone.length < 6) e.phone = "Please enter a valid phone number";
        if (!form.job_role) e.job_role = "Please complete this required field.";
        if (!form.property_name.trim()) e.property_name = "Please complete this required field.";
        if (!form.property_type) e.property_type = "Please complete this required field.";
        if (!form.country) e.country = "Please complete this required field.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (evt) => {
        evt.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            // reCAPTCHA v3 — execute invisibly, get score-backed token
            let token = "dev-bypass";
            if (RECAPTCHA_SITE_KEY) {
                if (!executeRecaptcha) {
                    toast.error("Verifikimi po ngarkohet — provo përsëri për një sekondë.");
                    setSubmitting(false);
                    return;
                }
                token = await executeRecaptcha("book_demo");
            }
            await api.post("/public/demo-requests", { ...form, recaptcha_token: token });
            setDone(true);
        } catch (err) {
            const detail = formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try again.";
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen w-full bg-white text-slate-900 antialiased" data-testid="signup-page">
            {/* Nav */}
            <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5">
                <Link to="/" className="flex items-center gap-2 text-[18px] font-bold tracking-tight">
                    <span className="text-[#F26322]">akomoda</span>
                </Link>
                <div className="flex items-center gap-4 text-[13px]">
                    <span className="hidden text-slate-500 sm:inline">Already have an account?</span>
                    <Link to="/login" className="rounded-xl border border-slate-200 px-3 py-1.5 font-semibold text-slate-700 transition hover:border-[#F26322] hover:text-[#F26322]" data-testid="signup-signin-link">
                        Sign in
                    </Link>
                </div>
            </nav>

            <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 pb-16 pt-2 lg:grid-cols-[minmax(0,1fr),580px]">
                {/* Left column — pitch */}
                <div className="hidden lg:flex lg:flex-col lg:pt-6">
                    <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#F26322]">
                        <Sparkles size={11} /> Book a demo
                    </div>
                    <h1 className="mt-4 text-[46px] font-black leading-[1.05] tracking-[-0.02em] text-slate-900">
                        Run your hotel<br />
                        <span className="text-[#F26322]">on autopilot.</span>
                    </h1>
                    <p className="mt-5 max-w-[480px] text-[15px] leading-relaxed text-slate-600">
                        Reservations, channel manager, POS, housekeeping, guest communication and
                        revenue reports — one unified system for hotels of any size across the Balkans and beyond.
                    </p>
                    <ul className="mt-8 space-y-3">
                        {[
                            "30-minute product walkthrough with a specialist",
                            "Setup guidance for your property size",
                            "Pricing tailored to your rooms and channels",
                            "No credit card required",
                        ].map((t) => (
                            <li key={t} className="flex items-start gap-2 text-[14px] text-slate-700">
                                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-500" />
                                {t}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-10 max-w-[440px] rounded-2xl border border-slate-100 bg-slate-50/60 p-5">
                        <div className="flex items-center gap-3">
                            <div className="grid h-10 w-10 place-items-center rounded-full bg-[#252E3D] text-[13px] font-bold text-white">BM</div>
                            <div>
                                <div className="text-[13px] font-bold">Besmir M.</div>
                                <div className="text-[11px] text-slate-500">Director · Airport Holiday Hotel</div>
                            </div>
                        </div>
                        <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">
                            &ldquo;Kaluam nga tri sisteme të ndryshme te Akomoda dhe stafi filloi ta përdorë brenda ditës.
                            Rezervimet nga Booking.com dhe Airbnb sinkronizohen automatikisht.&rdquo;
                        </p>
                    </div>
                </div>

                {/* Right column — form / success */}
                <div>
                    {done ? (
                        <SuccessCard email={form.email} onBack={() => navigate("/login")} />
                    ) : (
                        <form
                            onSubmit={submit}
                            className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.15)] sm:p-8"
                            data-testid="signup-form"
                            noValidate
                        >
                            <h2 className="text-[22px] font-bold tracking-tight text-slate-900">Tell us about your property</h2>
                            <p className="mt-1 text-[13px] text-slate-500">We&apos;ll reach out within 24 hours to schedule your demo.</p>

                            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <Input
                                    label="First name" required error={errors.first_name}
                                    value={form.first_name} onChange={(v) => set("first_name", v)}
                                    testid="signup-first-name"
                                />
                                <Input
                                    label="Last name" required error={errors.last_name}
                                    value={form.last_name} onChange={(v) => set("last_name", v)}
                                    testid="signup-last-name"
                                />
                            </div>

                            <Input
                                className="mt-4"
                                label="Email" required type="email" error={errors.email}
                                value={form.email} onChange={(v) => set("email", v)}
                                testid="signup-email"
                            />

                            <div className="mt-4">
                                <div className="mb-1.5 text-[12px] font-semibold text-slate-700">
                                    Phone number <span className="text-red-500">*</span>
                                </div>
                                <PhoneInput
                                    key={detectedCountry}
                                    defaultCountry={detectedCountry}
                                    value={form.phone}
                                    onChange={(e164, meta) => {
                                        set("phone", e164);
                                        set("phone_country", meta?.country || detectedCountry);
                                    }}
                                    testid="signup-phone"
                                    placeholder="69 123 4567"
                                />
                                {errors.phone && <FieldError>{errors.phone}</FieldError>}
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <Select
                                    label="Job role" required error={errors.job_role}
                                    value={form.job_role} onChange={(v) => set("job_role", v)}
                                    options={JOB_ROLES} testid="signup-job-role"
                                />
                                <Input
                                    label="Property name" required error={errors.property_name}
                                    value={form.property_name} onChange={(v) => set("property_name", v)}
                                    testid="signup-property-name"
                                />
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <Select
                                    label="Property type" required error={errors.property_type}
                                    value={form.property_type} onChange={(v) => set("property_type", v)}
                                    options={PROPERTY_TYPES} testid="signup-property-type"
                                />
                                <Select
                                    label="Country" required error={errors.country}
                                    value={form.country} onChange={(v) => set("country", v)}
                                    options={COUNTRIES.map((c) => ({ v: c, l: c }))}
                                    testid="signup-country"
                                />
                            </div>

                            <Input
                                className="mt-4"
                                label="How did you hear about us?"
                                value={form.heard_about} onChange={(v) => set("heard_about", v)}
                                testid="signup-heard-about"
                            />

                            {/* reCAPTCHA v3 — invisible; runs on submit */}
                            {RECAPTCHA_SITE_KEY && (
                                <div
                                    className="mt-5 flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 text-[11.5px] text-slate-500"
                                    data-testid="signup-recaptcha"
                                >
                                    <ShieldCheck size={14} className="shrink-0 text-emerald-500" />
                                    <span>
                                        Protected by Google reCAPTCHA (invisible). By continuing you agree to Google&apos;s{" "}
                                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy</a>
                                        {" & "}
                                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms</a>.
                                    </span>
                                </div>
                            )}

                            <p className="mt-5 text-[11.5px] leading-relaxed text-slate-500">
                                By submitting, you agree that Akomoda may use your details to contact you about our
                                platform. You can unsubscribe at any time. See our{" "}
                                <a href="https://akomoda.co/privacy" className="underline decoration-slate-300 underline-offset-2 hover:text-[#F26322]">
                                    Privacy Policy
                                </a>.
                            </p>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="signup-submit"
                                className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#F26322] px-6 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-white shadow-[0_16px_40px_-16px_rgba(242,99,34,0.55)] transition hover:-translate-y-[1px] hover:bg-[#e05715] disabled:opacity-60"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" /> Sending…
                                    </>
                                ) : (
                                    <>
                                        Book a demo <ArrowRight size={14} />
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

function FieldError({ children }) {
    return (
        <div className="mt-1.5 flex items-center gap-1 text-[11.5px] font-medium text-red-600">
            <span className="grid h-3.5 w-3.5 place-items-center rounded-full bg-red-100 text-[9px] font-bold">!</span>
            {children}
        </div>
    );
}

function Input({ label, required, value, onChange, error, type = "text", testid, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <div className="mb-1.5 text-[12px] font-semibold text-slate-700">
                {label}{required && <span className="text-red-500">*</span>}
            </div>
            <input
                type={type}
                value={value}
                data-testid={testid}
                onChange={(e) => onChange(e.target.value)}
                className={`h-12 w-full rounded-xl border px-4 text-[14px] text-slate-900 outline-none transition placeholder:text-slate-400 ${
                    error ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                        : "border-slate-200 focus:border-[#F26322] focus:ring-2 focus:ring-[#F26322]/15"
                }`}
            />
            {error && <FieldError>{error}</FieldError>}
        </label>
    );
}

function Select({ label, required, value, onChange, options, error, testid }) {
    return (
        <label className="block">
            <div className="mb-1.5 text-[12px] font-semibold text-slate-700">
                {label}{required && <span className="text-red-500">*</span>}
            </div>
            <div className="relative">
                <select
                    value={value}
                    data-testid={testid}
                    onChange={(e) => onChange(e.target.value)}
                    className={`h-12 w-full appearance-none rounded-xl border bg-white px-4 pr-10 text-[14px] outline-none transition ${
                        value ? "text-slate-900" : "text-slate-400"
                    } ${
                        error ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                            : "border-slate-200 focus:border-[#F26322] focus:ring-2 focus:ring-[#F26322]/15"
                    }`}
                >
                    <option value="" disabled>{label}{required ? "*" : ""}</option>
                    {options.map((o) => (
                        <option key={o.v} value={o.v}>{o.l}</option>
                    ))}
                </select>
                <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>
            {error && <FieldError>{error}</FieldError>}
        </label>
    );
}

function SuccessCard({ email, onBack }) {
    return (
        <div className="rounded-2xl border border-slate-200/70 bg-white p-8 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.15)]" data-testid="signup-success">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
                <CheckCircle2 size={26} strokeWidth={2.1} />
            </div>
            <h2 className="mt-5 text-[22px] font-bold tracking-tight text-slate-900">Thanks — we&apos;ll be in touch shortly.</h2>
            <p className="mt-2 text-[14px] leading-relaxed text-slate-600">
                We received your demo request and sent a confirmation to <strong className="text-slate-900">{email}</strong>.
                A member of our team will reach out within 24 hours to schedule a walkthrough at a time that works for you.
            </p>
            <button
                onClick={onBack}
                data-testid="signup-back-to-login"
                className="mt-6 inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-[13px] font-semibold text-slate-700 transition hover:border-[#F26322] hover:text-[#F26322]"
            >
                Back to sign in <ArrowRight size={13} />
            </button>
        </div>
    );
}
